import Slider from 'react-slick'
import styles from './AutoSlider.module.scss'
import styled from 'styled-components'
import Link from 'next/link'
import React, { useEffect, useRef } from 'react'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { gsap, Power1 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TweenVars = {
  y: '50',
  duration: 0.5,
  opacity: 0,
  ease: Power1.easeOut,
}

interface CaseStudyItem {
  projectName: string | React.ReactNode
  client: string
  description?: string
  src: string
  alt: string
  id: string
  tags: [
    ...{
      tag: string
      href: string
    }[],
  ]
  isOneline: boolean
  linkToDetail?: string
}

const caseStudyItems: CaseStudyItem[] = [
  {
    projectName: <>全3回のUX/UIワークショップで、プロジェクトを前に進める共通言語を獲得</>,
    client: '株式会社JTBビジネストラベルソリューションズ',
    src: '/images/img/case/img_thum_jtbcwt.webp',
    alt: '全3回のUX/UIワークショップで、プロジェクトを前に進める共通言語を獲得',
    id: 'jtbcwt',
    tags: [
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      { tag: 'サービス', href: '/cases/service/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/jtb-cwt/',
  },
  {
    projectName: <>AI時代の開発生産性を支えるデザインシステムを構築</>,
    client: '株式会社アルダグラム',
    src: '/images/img/case/img_thum_aldagram.webp',
    alt: 'AI時代の開発生産性を支えるデザインシステムを構築',
    id: 'aldagram',
    tags: [
      {
        tag: 'デザインシステム構築支援',
        href: '/cases/design-system/',
      },
      { tag: 'IT/情報/通信', href: '/cases/telecommunications/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/aldagram/',
  },
  {
    projectName: <>海外送金アプリを刷新し、ロイヤルティ向上と改修工数削減を実現</>,
    client: 'SBIレミット株式会社',
    src: '/images/img/case/img_thum_remit.webp',
    alt: '海外送金アプリを刷新し、ロイヤルティ向上と改修工数削減を実現',
    id: 'remit',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'フロントエンド開発',
        href: '/cases/frontend-dev/',
      },
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      { tag: '金融/不動産', href: '/cases/finance/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/remit/',
  },
  {
    projectName: (
      <>
        アジャイル開発により
        <br />
        Webサービスのアプリ化を2か月半で実現
      </>
    ),
    client: '株式会社SANU',
    src: '/images/img/case/img_thum_sanu.webp',
    alt: 'アジャイル開発によりWebサービスのアプリ化を2か月半で実現',
    id: 'sanu',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'フロントエンド開発',
        href: '/cases/frontend-dev/',
      },
      { tag: 'サービス', href: '/cases/service/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/sanu/',
  },
  {
    projectName: <>課題解決率が大幅向上 - “使える生成AIチャット”をアジャイル×UX/UIでスピード実現</>,
    client: '株式会社大和総研',
    src: '/images/img/case/img_thum_dir.webp',
    alt: '課題解決率が大幅向上 - “使える生成AIチャット”をアジャイル×UX/UIでスピード実現',
    id: 'dir',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'フロントエンド開発',
        href: '/cases/frontend-dev/',
      },
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      {
        tag: 'AI',
        href: '/cases/ai/',
      },
      { tag: '金融/不動産', href: '/cases/finance/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/dir/',
  },
  {
    projectName: (
      <>
        不動産売買の契約業務をDX化
        <br />- UX/UI刷新でミスを削減し、作業時間を大幅に短縮
      </>
    ),
    client: '株式会社不動産SHOP ナカジツ',
    src: '/images/img/case/img_thum_nakajitsu.webp',
    alt: '不動産売買の契約業務をDX化 - UX/UI刷新でミスを削減し、作業時間を大幅に短縮',
    id: 'nakajitsu',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'フロントエンド開発',
        href: '/cases/frontend-dev/',
      },
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      {
        tag: '内製化支援',
        href: '/cases/inhouse-support/',
      },
      { tag: '金融/不動産', href: '/cases/finance/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/nakajitsu/',
  },
]

const CustomSlide: React.FC<{ item: CaseStudyItem }> = ({ item }) => (
  <a href={item.linkToDetail} className={styles.casescard}>
    <div className={styles.casescard_img}>
      <img src={item.src} alt={item.alt} />
    </div>
    <div className={styles.casescard_label}>
      <p className={styles.casescard_label_name}>{item.projectName}</p>
      <p className={styles.casescard_label_client}>{item.client}</p>
      <ul className={styles.casescard_label_category}>
        {item.tags.map((tagItem, index) => (
          <li
            key={`${tagItem.tag}-${index}`}
            className={`${
              ['IT/情報/通信', 'メーカー/製造/建設', '金融/不動産', '小売/流通/卸', 'その他', 'サービス'].includes(
                tagItem.tag,
              )
                ? styles.casescard_label_category_white
                : styles.casescard_label_category_gray
            }`}
          >
            <a href={tagItem.href} className={styles.categoryButton} onClick={(e) => e.stopPropagation()}>
              {tagItem.tag}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </a>
)

const AutoSlider: React.FC = () => {
  const sliderRef = useRef(null)
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    arrows: false,
    autoplaySpeed: 8000,
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 720,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 620,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
    onInit: () => setEqualHeight(),
    onReInit: () => setEqualHeight(),
  }

  const setEqualHeight = () => {
    if (!sliderRef.current) return
    let maxHeight = 0
    const slides = sliderRef.current.querySelectorAll('.slick-slide')
    slides.forEach((slide) => {
      slide.style.height = 'auto'
      const height = slide.clientHeight
      maxHeight = Math.max(maxHeight, height)
    })
    slides.forEach((slide) => {
      slide.style.height = `${maxHeight}px`
    })
  }

  // スライダーのアニメーション
  useEffect(() => {
    const timeline = gsap.timeline()

    if (sliderRef.current) {
      timeline.from(sliderRef.current, tweenVars)

      ScrollTrigger.create({
        animation: timeline,
        trigger: sliderRef.current,
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      })
    }
  }, [sliderRef])

  useEffect(() => {
    setEqualHeight()
    window.addEventListener('resize', setEqualHeight)
    return () => {
      window.removeEventListener('resize', setEqualHeight)
    }
  }, [])

  return (
    <SliderContainer>
      <SliderWrapper className={styles.slider} ref={sliderRef}>
        <Slider {...settings}>
          {caseStudyItems.map((item) =>
            item.linkToDetail ? (
              <Link href={item.linkToDetail} key={item.id}>
                <CustomSlide item={item} />
              </Link>
            ) : (
              <CustomSlide item={item} key={item.id} />
            ),
          )}
        </Slider>
      </SliderWrapper>
    </SliderContainer>
  )
}

const SliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  margin: 64px 0;
  @media (max-width: 960px) {
    margin: 48px 0;
  }
`

const SliderWrapper = styled.div`
  width: 100%;
  padding: 0 24px;
  @media (min-width: 720px) and (max-width: 1199px) {
    padding: 0 40px;
  }
  @media (min-width: 1200px) {
    width: 82%;
    max-width: 1180px;
    margin: 0 auto;
    padding: 0;
  }

  .slick-dots {
    margin: 20px auto 0;
    position: relative;
    bottom: 0;
    text-align: left;
    @media (max-width: 1199px) {
    }
    @media (min-width: 1200px) {
      margin: 27px auto 0;
    }
  }

  .slick-dots li {
    position: relative;
    width: 40px;
    height: 24px;
    margin: 0 0 0 8px;
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 4px;
      top: 0;
      bottom: 0;
      margin: auto 0;
      background: #ccc;
      pointer-events: none;
    }
    @media (max-width: 719px) {
      width: 32px;
    }
  }

  .slick-dots li:first-child {
    margin: 0;
  }

  .slick-dots li.slick-active::after {
    background: #db0032;
  }

  .slick-dots li button {
    width: 100%;
    height: 100%;
    padding: 0;
    background: transparent;
  }

  .slick-dots li button::before {
    content: none;
  }
  .slick-slide > div,
  .slick-slide > div > a {
    height: 100%;
  }
  .slick-list {
    overflow: visible;
  }
`

export default AutoSlider
